import { MoneyText } from '@/components/MoneyText';
import { errorCopy } from '@/api/errorCopy';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import {
  reimbursementsApi,
  type ReimbursementCandidate,
  type ReimbursementCredit,
  type ReimbursementLink,
} from '@/api/reimbursements';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { ConfirmButton } from '@/components/ConfirmButton';
import {
  formatCivilDate,
  formatMoneyMinor,
  minorToInputText,
  parseMoneyToMinor,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useOwnedEnvelopes } from './useOwnedEnvelopes';
import { ReimbursementTargetPicker } from './ReimbursementTargetPicker';
import { MatchPill } from './MatchPill';
import { matchWords, type MatchFacts } from './reimbursementMatch';
import { registerNear } from './registerNear';
import { SubmitButton } from '@/components/SubmitButton';

interface ReimbursementPanelProps {
  account: Account;
  line: OwnerRegisterLine;
  operatingCurrency: string | null;
  onChanged: (line: OwnerRegisterLine) => void | Promise<void>;
  onNotify: (message: string) => void;
  onSubscriptionRequired: () => void;
  /** When set (e.g. from the review queue), jump straight into selection. */
  preferExpenseTransactionId?: number | null;
}

/**
 * Incoming-line reimbursement treatment body (TMMAPP-0031): candidates with their match chip,
 * one confirm write path per pair, optional amount / envelope override, manual search, unlink.
 */
export function ReimbursementPanel({
  account,
  line,
  operatingCurrency,
  onChanged,
  onNotify,
  onSubscriptionRequired,
  preferExpenseTransactionId = null,
}: ReimbursementPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { envelopes, reload: reloadEnvelopes } = useOwnedEnvelopes();
  const [credit, setCredit] = useState<ReimbursementCredit | null>(null);
  const [candidates, setCandidates] = useState<ReimbursementCandidate[]>([]);
  const [proposedByExpense, setProposedByExpense] = useState<Map<number, ReimbursementLink>>(
    () => new Map(),
  );
  const [confirmed, setConfirmed] = useState<ReimbursementLink | null>(null);
  const [confirmedPeer, setConfirmedPeer] = useState<OwnerRegisterLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedExpenseId, setSelectedExpenseId] = useState<number | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [amountText, setAmountText] = useState('');
  const [envelopeOverride, setEnvelopeOverride] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The link a confirm was refused on for want of an envelope, and that attempt's amount (item 3).
  const [target, setTarget] = useState<{ linkId: number; amountMinor: number | undefined } | null>(
    null,
  );
  const [manualOpen, setManualOpen] = useState(false);
  const [manualQuery, setManualQuery] = useState('');
  const [manualLines, setManualLines] = useState<{ account: Account; line: OwnerRegisterLine }[]>(
    [],
  );
  // Bumped to read the links and candidates afresh after a payback changed under a write.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setLoading(true);
      void (async () => {
        try {
          const [cand, proposed, confirmedPage] = await Promise.all([
            reimbursementsApi.candidates(line.id).catch(() => null),
            reimbursementsApi.list('PROPOSED').catch(() => ({ items: [], nextCursor: null })),
            reimbursementsApi.list('CONFIRMED').catch(() => ({ items: [], nextCursor: null })),
          ]);
          if (cancelled) return;
          const proposedMap = new Map<number, ReimbursementLink>();
          for (const link of proposed.items) {
            if (link.creditTransactionId === line.id || link.expenseTransactionId === line.id) {
              proposedMap.set(link.expenseTransactionId, link);
            }
          }
          setProposedByExpense(proposedMap);
          const hit =
            confirmedPage.items.find(
              (l) => l.creditTransactionId === line.id || l.expenseTransactionId === line.id,
            ) ?? null;
          setConfirmed(hit);
          if (hit !== null) {
            // The link names the peer's account (TMMAPP-0097 B7): one request, no probing.
            const creditSide = hit.creditTransactionId === line.id;
            const peerId = creditSide ? hit.expenseTransactionId : hit.creditTransactionId;
            const peerAccountId = creditSide ? hit.expenseAccountId : hit.creditAccountId;
            if (peerAccountId !== null) {
              try {
                const peer = await transactionsApi.get(peerAccountId, peerId);
                if (!cancelled) setConfirmedPeer(peer);
              } catch {
                /* the linked transaction is unavailable */
              }
            }
          }
          if (cand !== null) {
            setCredit(cand.credit);
            setCandidates(cand.candidates);
            // NEAR is never pre-selected — only an explicit prefer (e.g. queue) may select a row.
            const prefer =
              preferExpenseTransactionId !== null &&
              cand.candidates.some((c) => c.expenseTransactionId === preferExpenseTransactionId)
                ? preferExpenseTransactionId
                : null;
            setSelectedExpenseId(prefer);
            const first =
              prefer !== null
                ? cand.candidates.find((c) => c.expenseTransactionId === prefer)
                : undefined;
            const defaultAmount = first?.amountMinor ?? cand.credit.amountMinor;
            setAmountText(
              minorToInputText(
                Math.abs(defaultAmount),
                cand.credit.currency || operatingCurrency,
                locale,
              ),
            );
            if (first?.envelopeId != null) setEnvelopeOverride(first.envelopeId);
          } else {
            setCredit(null);
            setCandidates([]);
          }
        } catch {
          if (!cancelled) {
            setCredit(null);
            setCandidates([]);
            setConfirmed(null);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    });
    return () => {
      cancelled = true;
    };
  }, [line.id, locale, operatingCurrency, preferExpenseTransactionId, reloadKey]);

  const selected =
    selectedExpenseId === null
      ? null
      : (candidates.find((c) => c.expenseTransactionId === selectedExpenseId) ?? null);

  /** Confirms the link; the rest of the panel follows from the confirmed link. */
  async function sendConfirm(
    linkId: number,
    amountMinor: number | undefined,
    envelopeId: number | undefined,
  ) {
    const body: { amountMinor?: number; envelopeId?: number } = {};
    if (amountMinor !== undefined) body.amountMinor = amountMinor;
    if (envelopeId !== undefined) body.envelopeId = envelopeId;
    const confirmedLink = await reimbursementsApi.confirm(
      linkId,
      Object.keys(body).length > 0 ? body : undefined,
    );
    setTarget(null);
    const updated = await transactionsApi.get(account.id, line.id);
    await onChanged(updated);
    await reloadEnvelopes();
    const envelope =
      envelopes.find((v) => v.id === (envelopeId ?? selected?.envelopeId))?.name ??
      selected?.categoryName ??
      '';
    onNotify(t('reimb.confirmed', { envelope }));
    setConfirmed(confirmedLink);
  }

  /**
   * A link, confirm or unlink that met another change to the payback (`409 CONCURRENT_UPDATE`,
   * TMMAPI-0180) says so in the payback's words, not the envelope's, and refetches the line and the
   * panel (TMMAPP-0144 item 1). The notice outlives the panel's remount on a changed line.
   */
  async function paybackChanged() {
    onNotify(t('reimb.changed'));
    setTarget(null);
    setReloadKey((key) => key + 1);
    try {
      await onChanged(await transactionsApi.get(account.id, line.id));
    } catch {
      /* the line is unavailable; the panel's own refetch still runs */
    }
  }

  /**
   * A confirm refused for want of an envelope (`REIMBURSEMENT_TARGET_REQUIRED`, TMMAPI-0179) asks for
   * one in place, with the link and the amount of this attempt kept for the resend (TMMAPP-0140
   * item 3).
   */
  function handleConfirmError(
    err: unknown,
    linkId: number | null,
    amountMinor: number | undefined,
  ) {
    if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
      onSubscriptionRequired();
    } else if (
      isApiError(err) &&
      err.code === ErrorCodes.REIMBURSEMENT_TARGET_REQUIRED &&
      linkId !== null
    ) {
      setTarget({ linkId, amountMinor });
    } else if (isApiError(err) && err.code === ErrorCodes.CONCURRENT_UPDATE) {
      void paybackChanged();
    } else {
      setError(errorCopy(t, err, 'accounts.errorGeneric'));
    }
  }

  async function confirmPair(
    expenseTransactionId: number,
    existing: ReimbursementLink | null,
    amountMinor: number | undefined,
    envelopeId: number | undefined,
  ) {
    setSaving(true);
    setError(null);
    // A new attempt (another candidate, a manual pick) drops an earlier envelope question.
    setTarget(null);
    let linkId: number | null = existing?.id ?? null;
    try {
      if (linkId === null) {
        // One write path: confirm an existing PROPOSED link — never link again. A link made here is
        // remembered as proposed, so a confirm refused after it doesn't link a second time.
        const linked = await reimbursementsApi.link({
          expenseTransactionId,
          creditTransactionId: line.id,
          ...(amountMinor !== undefined ? { amountMinor } : {}),
        });
        linkId = linked.id;
        setProposedByExpense((prev) => new Map(prev).set(expenseTransactionId, linked));
      }
      await sendConfirm(linkId, amountMinor, envelopeId);
    } catch (err) {
      handleConfirmError(err, linkId, amountMinor);
    } finally {
      setSaving(false);
    }
  }

  async function handleTargetConfirm(envelopeId: number) {
    if (target === null || saving) return;
    setSaving(true);
    setError(null);
    try {
      await sendConfirm(target.linkId, target.amountMinor, envelopeId);
    } catch (err) {
      handleConfirmError(err, target.linkId, target.amountMinor);
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirm() {
    if (selectedExpenseId === null || saving) return;
    const currency = credit?.currency || operatingCurrency;
    let amountMinor: number | undefined;
    if (adjustOpen) {
      const parsed = parseMoneyToMinor(amountText, currency, locale);
      if (parsed === null) {
        setError(t('accounts.errorValidation'));
        return;
      }
      amountMinor = Math.abs(parsed);
    }
    const defaultEnvelope = selected?.envelopeId ?? null;
    const overrideEnvelope =
      typeof envelopeOverride === 'number' && envelopeOverride !== defaultEnvelope
        ? envelopeOverride
        : undefined;
    const existing = proposedByExpense.get(selectedExpenseId) ?? null;
    await confirmPair(selectedExpenseId, existing, amountMinor, overrideEnvelope);
  }

  async function handleUnlink() {
    if (confirmed === null) return;
    try {
      await reimbursementsApi.unlink(confirmed.id);
      const updated = await transactionsApi.get(account.id, line.id);
      await onChanged(updated);
      await reloadEnvelopes();
      onNotify(t('reimb.unlinked'));
      setConfirmed(null);
      setConfirmedPeer(null);
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.CONCURRENT_UPDATE) void paybackChanged();
      else onNotify(errorCopy(t, err, 'accounts.errorGeneric'));
    }
  }

  async function openManualSearch() {
    setManualOpen(true);
    try {
      const accounts = (await accountsApi.list()).filter((a) => a.access === 'OWNER');
      const pages = await Promise.all(
        accounts.map(async (acct) => {
          try {
            const rows = await registerNear(
              acct.id,
              line.postedOn,
              (row) => row.status === 'POSTED' && row.operatingAmountMinor < 0,
            );
            return rows.map((row) => ({ account: acct, line: row }));
          } catch {
            return [];
          }
        }),
      );
      setManualLines(pages.flat());
    } catch {
      setManualLines([]);
    }
  }

  async function handleManualPick(expenseId: number) {
    const currency = credit?.currency || operatingCurrency;
    let amountMinor: number | undefined;
    if (adjustOpen) {
      const parsed = parseMoneyToMinor(amountText, currency, locale);
      if (parsed === null) {
        setError(t('accounts.errorValidation'));
        return;
      }
      amountMinor = Math.abs(parsed);
    }
    const overrideEnvelope = typeof envelopeOverride === 'number' ? envelopeOverride : undefined;
    const existing = proposedByExpense.get(expenseId) ?? null;
    await confirmPair(expenseId, existing, amountMinor, overrideEnvelope);
  }

  if (loading) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('common.loading')}
      </Typography>
    );
  }

  if (confirmed !== null) {
    const isCredit = confirmed.creditTransactionId === line.id;
    const peer = confirmedPeer;
    const chipLabel =
      isCredit && peer !== null
        ? t('reimb.chipCredit', {
            payee: peer.payee,
            date: formatCivilDate(peer.postedOn, locale),
          })
        : !isCredit && peer !== null
          ? t('reimb.chipExpense', {
              amount: formatMoneyMinor(
                Math.abs(peer.operatingAmountMinor),
                operatingCurrency,
                locale,
              ),
            })
          : t('reimb.title');
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2">{chipLabel}</Typography>
        <ConfirmButton
          size="small"
          color="warning"
          onConfirm={() => void handleUnlink()}
          aria-description={t('reimb.unlinkConfirm')}
        >
          {t('reimb.unlink')}
        </ConfirmButton>
      </Stack>
    );
  }

  const currency = credit?.currency || operatingCurrency;
  // A candidate already proposed as a link carries the link's reason; a bare one only its grade.
  const candidateMatch = (row: ReimbursementCandidate): MatchFacts =>
    proposedByExpense.get(row.expenseTransactionId) ?? {
      grade: row.grade,
      reasonCode: null,
      fractionDenominator: null,
    };
  const candidateWords = (row: ReimbursementCandidate) =>
    matchWords(t, candidateMatch(row), () =>
      formatMoneyMinor(
        Math.abs(Math.abs(row.amountMinor) - Math.abs(credit?.amountMinor ?? 0)),
        currency,
        locale,
      ),
    );

  return (
    <Stack spacing={1.5}>
      {error !== null && <Alert severity="error">{error}</Alert>}
      {credit !== null && (
        <Typography variant="body2">
          <TabularText variant="body2">{formatCivilDate(credit.postedOn, locale)}</TabularText>
          {' · '}
          {credit.payee}
          {' · '}
          <MoneyText minor={credit.amountMinor} currency={currency} />
        </Typography>
      )}

      {candidates.length === 0 ? (
        <Alert severity="info">{t('reimb.noCandidates')}</Alert>
      ) : (
        <RadioGroup
          value={selectedExpenseId === null ? '' : String(selectedExpenseId)}
          onChange={(_e, value) => {
            const id = Number(value);
            const row = candidates.find((c) => c.expenseTransactionId === id);
            // Refuse to silently select NEAR as a default — user must click it.
            if (!row) return;
            setSelectedExpenseId(id);
            // The envelope question belongs to the link it was asked for, not this candidate.
            setTarget(null);
            setAmountText(
              minorToInputText(
                Math.abs(row.amountMinor),
                credit?.currency || operatingCurrency,
                locale,
              ),
            );
            setEnvelopeOverride(row.envelopeId ?? '');
          }}
        >
          {candidates.map((row) => (
            <Box key={row.expenseTransactionId} sx={{ mb: 1 }}>
              <FormControlLabel
                value={String(row.expenseTransactionId)}
                control={<Radio />}
                label={
                  <Stack spacing={0.25}>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TabularText>{formatCivilDate(row.postedOn, locale)}</TabularText>
                      <Typography variant="body2" component="span">
                        {t('reimb.dayGap', { count: row.dayGap })}
                      </Typography>
                      <Typography variant="body2" component="span">
                        {row.payee}
                      </Typography>
                      <MoneyText minor={row.amountMinor} currency={currency} />
                      {row.categoryName !== null && (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ color: 'text.secondary' }}
                        >
                          {row.categoryName}
                        </Typography>
                      )}
                      {/* The pair card's match chip, with no server prose and no alert (item 12, TMM-B3). */}
                      <MatchPill match={candidateMatch(row)} label={candidateWords(row)} />
                    </Stack>
                  </Stack>
                }
              />
            </Box>
          ))}
        </RadioGroup>
      )}

      {target !== null ? (
        <ReimbursementTargetPicker
          busy={saving}
          onConfirm={(envelopeId) => void handleTargetConfirm(envelopeId)}
          onCancel={() => setTarget(null)}
        />
      ) : (
        selected !== null && (
          <Stack spacing={1}>
            <Button size="small" onClick={() => setAdjustOpen((v) => !v)}>
              {t('reimb.adjustAmount')}
            </Button>
            <Collapse in={adjustOpen}>
              <TextField
                size="small"
                label={t('reimb.adjustAmount')}
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                fullWidth
              />
            </Collapse>
            <FormControl fullWidth size="small">
              <InputLabel id="reimb-envelope-label">{t('reimb.envelopeOverride')}</InputLabel>
              <Select
                labelId="reimb-envelope-label"
                label={t('reimb.envelopeOverride')}
                value={envelopeOverride === '' ? '' : String(envelopeOverride)}
                onChange={(event) => {
                  const raw = event.target.value;
                  setEnvelopeOverride(raw === '' ? '' : Number(raw));
                }}
              >
                {envelopes.map((envelope) => (
                  <MenuItem key={envelope.id} value={String(envelope.id)}>
                    {envelope.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <SubmitButton
              loading={saving}
              disabled={selectedExpenseId === null}
              onClick={() => void handleConfirm()}
            >
              {t('reimb.confirm')}
            </SubmitButton>
          </Stack>
        )
      )}

      <Box>
        <Button size="small" onClick={() => void openManualSearch()}>
          {t('reimb.manualSearch')}
        </Button>
        {manualOpen && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label={t('reimb.manualSearch')}
              value={manualQuery}
              onChange={(e) => setManualQuery(e.target.value)}
              fullWidth
            />
            {manualLines
              .filter((row) => {
                const q = manualQuery.trim().toLowerCase();
                if (q.length === 0) return true;
                return row.line.payee.toLowerCase().includes(q);
              })
              .map((row) => (
                <Stack
                  key={`${row.account.id}-${row.line.id}`}
                  direction="row"
                  sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <Typography variant="body2">
                    {formatCivilDate(row.line.postedOn, locale)} · {row.line.payee} ·{' '}
                    <MoneyText minor={row.line.operatingAmountMinor} currency={operatingCurrency} />
                  </Typography>
                  <Button size="small" onClick={() => void handleManualPick(row.line.id)}>
                    {t('reimb.confirm')}
                  </Button>
                </Stack>
              ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
